'use client';

import { useEffect, useState } from 'react';
import { List } from 'lucide-react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

export default function BlogTableOfContents() {
  const [headings, setHeadings] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState('');

  /* extract headings from rendered article */
  useEffect(() => {
    const article = document.querySelector('.blog-content');
    if (!article) return;

    const els = article.querySelectorAll('h2, h3');
    const items: TocItem[] = [];

    els.forEach((el) => {
      // generate a slug-based id from heading text
      if (!el.id) {
        const text = (el.textContent || '').trim();
        el.id = text
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .slice(0, 60);
      }
      items.push({
        id: el.id,
        text: el.textContent || '',
        level: el.tagName === 'H2' ? 2 : 3,
      });
    });

    setHeadings(items);
  }, []);

  /* track active heading on scroll */
  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0.1 }
    );

    headings.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav className="space-y-1" aria-label="Table of contents">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
        <List className="h-4 w-4 text-jasper" />
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-400">
          Contents
        </span>
      </div>

      {headings.map((item) => (
        <a
          key={item.id}
          href={`#${item.id}`}
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById(item.id);
            if (el) {
              const y = el.getBoundingClientRect().top + window.scrollY - 100;
              window.scrollTo({ top: y, behavior: 'smooth' });
            }
          }}
          className={`block text-[13px] leading-snug py-1.5 transition-all duration-200 border-l-2 ${
            item.level === 3 ? 'pl-6' : 'pl-3'
          } ${
            activeId === item.id
              ? 'border-jasper text-jasper font-medium'
              : 'border-transparent text-gray-400 hover:text-charcoal hover:border-gray-200'
          }`}
        >
          {item.text}
        </a>
      ))}
    </nav>
  );
}
